import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, Phone, Search, X } from "lucide-react";
import { NAV_LINKS, PHONE_DISPLAY, PHONE_TEL } from "@/lib/site";
import { useEnquiry } from "@/components/EnquiryModal";

const MAIN_NAV = NAV_LINKS.slice(0, 7);
const MORE_NAV = NAV_LINKS.slice(7);

const NAV_TESTIDS = {
  "/international-holidays": "header-nav-destinations",
  "/visa": "header-nav-visa",
  "/group-tours": "header-nav-group-tours",
  "/about": "header-nav-about",
  "/contact": "header-nav-contact",
};

const linkCls = ({ isActive }) =>
  `text-[13px] font-semibold tracking-wide transition-colors hover:text-royal ${
    isActive ? "text-royal" : "text-slate-700"
  }`;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openEnquiry } = useEnquiry();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <header data-testid="site-header"
      className={`sticky top-0 z-50 h-20 bg-white/85 backdrop-blur-xl border-b border-slate-200/80 transition-shadow duration-300 ${scrolled ? "shadow-md" : "shadow-sm"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-brand-logo" className="flex items-center shrink-0">
          <img src="/logo-header.png" alt="D Travel Link — Linking Dreams to Destinations" className="h-16 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-5" aria-label="Main navigation">
          {MAIN_NAV.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={NAV_TESTIDS[l.to] || `header-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={linkCls}>
              {l.label}
            </NavLink>
          ))}
          <div className="relative group">
            <button data-testid="header-nav-more" className="flex items-center gap-1 text-[13px] font-semibold tracking-wide text-slate-700 hover:text-royal transition-colors">
              More <ChevronDown className="w-3.5 h-3.5" />
            </button>
            <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity absolute right-0 top-full pt-3">
              <div className="w-48 rounded-xl border border-slate-200 bg-white shadow-xl py-2">
                {MORE_NAV.map((l) => (
                  <NavLink key={l.to} to={l.to} data-testid={NAV_TESTIDS[l.to] || `header-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                           className="block px-4 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-royal transition-colors">
                    {l.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <Link to="/search" data-testid="header-search-button" aria-label="Search"
                className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:text-royal hover:border-royal transition-colors">
            <Search className="w-4 h-4" />
          </Link>
          <a href={PHONE_TEL} data-testid="header-phone-link"
             className="flex items-center gap-2 text-sm font-semibold text-navy hover:text-royal transition-colors">
            <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
          </a>
          <button onClick={() => openEnquiry({})} data-testid="header-get-quote-button"
                  className="rounded-full bg-navy px-5 py-2.5 text-sm font-heading font-bold text-white hover:bg-royal transition-colors">
            Get a Quote
          </button>
        </div>

        <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu"
                className="lg:hidden w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-navy">
          <Menu className="w-5 h-5" />
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="fixed inset-0 z-[60] bg-navydeep/60 backdrop-blur-sm lg:hidden" onClick={() => setMobileOpen(false)}>
            <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-white shadow-2xl flex flex-col" data-lenis-prevent
                        onClick={(e) => e.stopPropagation()} data-testid="mobile-menu">
              <div className="flex items-center justify-between px-5 h-20 border-b border-slate-100">
                <img src="/logo-header.png" alt="D Travel Link" className="h-12 w-auto" />
                <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu"
                        className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-navy">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto px-5 py-4" aria-label="Mobile navigation">
                {NAV_LINKS.map((l) => (
                  <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                           className={({ isActive }) =>
                             `block py-3 border-b border-slate-100 text-[15px] font-semibold ${isActive ? "text-royal" : "text-slate-800"}`}>
                    {l.label}
                  </NavLink>
                ))}
              </nav>
              <div className="px-5 py-4 border-t border-slate-100 space-y-3">
                <a href={PHONE_TEL} className="flex items-center gap-2 text-sm font-semibold text-navy">
                  <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
                </a>
                <button onClick={() => { setMobileOpen(false); openEnquiry({}); }} data-testid="mobile-get-quote-button"
                        className="w-full rounded-full bg-navy py-3 text-sm font-heading font-bold text-white">
                  Get a Quote
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
